/**
 * TratamientoPublico.js
 * Vista pública para ver el progreso de un tratamiento via QR/link
 * No requiere autenticación - usa token temporal
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_URL } from '../services/api/config';

export default function TratamientoPublico() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    async function cargarTratamiento() {
      if (!id || !token) {
        setError('Enlace inválido');
        setLoading(false);
        return;
      }

      try {
        const resp = await fetch(`${API_URL}/api/tratamientos/${id}/publico?token=${token}`);
        const json = await resp.json();

        if (!resp.ok) {
          setError(json.error || 'No se pudo cargar el tratamiento');
          return;
        }

        setData(json);
      } catch (err) {
        console.error('Error cargando tratamiento:', err);
        setError('Error de conexión');
      } finally {
        setLoading(false);
      }
    }

    cargarTratamiento();
  }, [id, token]);

  // Formatear fecha
  function formatearFecha(fechaStr) {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr + 'T12:00:00');
    return fecha.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short'
    });
  }

  // Formatear hora a 12h
  function formatearHora(hora) {
    if (!hora) return '';
    const [h, m] = hora.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m || '00'} ${ampm}`;
  }

  // Calcular horarios sugeridos basados en frecuencia
  function calcularHorarios(frecuenciaHoras) {
    const horarios = {
      24: ['08:00'],
      12: ['08:00', '20:00'],
      8: ['06:00', '14:00', '22:00'],
      6: ['06:00', '12:00', '18:00', '00:00'],
      4: ['06:00', '10:00', '14:00', '18:00', '22:00', '02:00']
    };
    return horarios[frecuenciaHoras] || horarios[12];
  }

  // Tiempo desde última actualización
  function tiempoDesde(fechaStr) {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    const ahora = new Date();
    const diffMs = ahora - fecha;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHoras = Math.floor(diffMins / 60);
    const diffDias = Math.floor(diffHoras / 24);

    if (diffMins < 1) return 'ahora mismo';
    if (diffMins < 60) return `hace ${diffMins} minuto${diffMins !== 1 ? 's' : ''}`;
    if (diffHoras < 24) return `hace ${diffHoras} hora${diffHoras !== 1 ? 's' : ''}`;
    return `hace ${diffDias} día${diffDias !== 1 ? 's' : ''}`;
  }

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingCard}>
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p style={{ color: '#666' }}>Cargando tratamiento...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>
            {error.includes('expirado') ? '⏰' : '🔒'}
          </div>
          <h3 style={{ marginBottom: '12px', color: '#333' }}>
            {error.includes('expirado') ? 'Enlace expirado' : 'Acceso denegado'}
          </h3>
          <p style={{ color: '#666', marginBottom: '24px' }}>{error}</p>
          <p style={{ color: '#999', fontSize: '14px' }}>
            Contacta al dueño de la mascota para obtener un nuevo enlace.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const {
    nombreMascota,
    fechaInicio,
    fechaFin,
    diaActual,
    diasTotales,
    porcentaje,
    estado,
    medicamentos,
    ultimaActualizacion
  } = data;

  const terminado = estado === 'completado' || diaActual > diasTotales;

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Header con logo */}
        <div style={styles.header}>
          <span style={{ fontSize: '28px' }}>🍋</span>
          <span style={styles.logo}>LemmonTags</span>
        </div>

        {/* Card principal */}
        <div style={styles.mainCard}>
          {/* Titulo */}
          <div style={styles.titleSection}>
            <span style={{ fontSize: '32px', marginRight: '12px' }}>🐕</span>
            <div>
              <h1 style={styles.title}>Tratamiento de {nombreMascota}</h1>
              <p style={styles.dates}>
                📅 {formatearFecha(fechaInicio)} - {formatearFecha(fechaFin)} {new Date(fechaFin + 'T12:00:00').getFullYear()}
              </p>
            </div>
          </div>

          {/* Barra de progreso */}
          <div style={styles.progressSection}>
            <div style={styles.progressBar}>
              <div
                style={{
                  ...styles.progressFill,
                  width: `${porcentaje}%`
                }}
              />
            </div>
            <div style={styles.progressInfo}>
              <span style={styles.percentage}>{porcentaje}%</span>
              <span style={styles.dayInfo}>
                {terminado ? '¡Completado!' : `Día ${diaActual} de ${diasTotales}`}
              </span>
            </div>
          </div>

          {/* Estado especial si terminó */}
          {terminado && (
            <div style={styles.completedBanner}>
              <span style={{ fontSize: '24px', marginRight: '8px' }}>🎉</span>
              ¡Tratamiento completado exitosamente!
            </div>
          )}
        </div>

        {/* Medicamentos */}
        <div style={styles.medsCard}>
          <h2 style={styles.medsTitle}>💊 Medicamentos</h2>

          {medicamentos && medicamentos.length > 0 ? (
            <div style={styles.medsList}>
              {medicamentos.map((med, idx) => {
                const horarios = calcularHorarios(med.frecuenciaHoras);
                return (
                  <div key={idx} style={styles.medItem}>
                    <div style={styles.medName}>
                      {med.nombre} {med.dosis && <span style={styles.medDosis}>{med.dosis}</span>}
                    </div>
                    <div style={styles.medHorarios}>
                      ⏰ {horarios.map(h => formatearHora(h)).join(' • ')}
                    </div>
                    {med.instrucciones && (
                      <div style={styles.medInstrucciones}>
                        📝 {med.instrucciones}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ color: '#999', textAlign: 'center' }}>
              No hay medicamentos registrados
            </p>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <a
            href="https://lemmontags.com"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.footerLink}
          >
            LemmonTags.com
          </a>
          <p style={styles.footerText}>
            Actualizado: {tiempoDesde(ultimaActualizacion)}
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center'
  },
  content: {
    width: '100%',
    maxWidth: '500px'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px 0',
    borderBottom: '1px solid #e5e7eb',
    marginBottom: '20px'
  },
  logo: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#333'
  },
  loadingCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '48px',
    textAlign: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  errorCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '48px 32px',
    textAlign: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    maxWidth: '400px'
  },
  mainCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    marginBottom: '16px'
  },
  titleSection: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111',
    margin: 0,
    lineHeight: 1.3
  },
  dates: {
    fontSize: '14px',
    color: '#666',
    margin: '8px 0 0'
  },
  progressSection: {
    marginBottom: '16px'
  },
  progressBar: {
    backgroundColor: '#e5e7eb',
    borderRadius: '100px',
    height: '24px',
    overflow: 'hidden',
    marginBottom: '12px'
  },
  progressFill: {
    backgroundColor: '#00BCD4',
    height: '100%',
    borderRadius: '100px',
    transition: 'width 0.5s ease'
  },
  progressInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  percentage: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#00BCD4'
  },
  dayInfo: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '500'
  },
  completedBanner: {
    backgroundColor: '#f0fdf4',
    color: '#16a34a',
    padding: '12px 16px',
    borderRadius: '8px',
    textAlign: 'center',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  medsCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    marginBottom: '16px'
  },
  medsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 16px'
  },
  medsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  medItem: {
    borderBottom: '1px solid #f3f4f6',
    paddingBottom: '16px'
  },
  medName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111',
    marginBottom: '6px'
  },
  medDosis: {
    fontWeight: '400',
    color: '#666'
  },
  medHorarios: {
    fontSize: '14px',
    color: '#00BCD4',
    marginBottom: '4px'
  },
  medInstrucciones: {
    fontSize: '13px',
    color: '#666',
    fontStyle: 'italic'
  },
  footer: {
    textAlign: 'center',
    padding: '24px 0',
    borderTop: '1px solid #e5e7eb'
  },
  footerLink: {
    color: '#00BCD4',
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '14px'
  },
  footerText: {
    color: '#999',
    fontSize: '12px',
    marginTop: '8px'
  }
};
